import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ArrowRight, TrendingUp, MessageCircle } from 'lucide-react';
import {
  creativeWorkbenchApi,
  type WorkbenchPersonaLanesResponse,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const CURRENCY_COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface PersonaLanesRailProps {
  /**
   * Chat-analytics-ui Phase 4 — opens the EmbeddedChatPanel seeded with the
   * lane's persona / metric context when the user clicks "Ask".
   */
  onAskAboutLane?: (seed: ChatSeedInput) => void;
}

export function PersonaLanesRail({ onAskAboutLane }: PersonaLanesRailProps = {}) {
  const navigate = useNavigate();
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchPersonaLanesResponse | null>(null);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    creativeWorkbenchApi
      .getPersonaLanes({ clientUid })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid]);

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Users size={14} className="text-teal-500" />
          Active Persona Lanes
        </h2>
        <span className="text-[10px] font-mono text-slate-400">spend share</span>
      </header>
      {!data && <div className="text-xs text-slate-400">Loading...</div>}
      {data && data.lanes.length === 0 && (
        <div className="text-xs text-slate-400">No active personas yet.</div>
      )}
      <ul className="divide-y divide-slate-100">
        {(data?.lanes ?? []).map((lane) => {
          const total = data?.lanes.reduce((s, l) => s + l.spend, 0) ?? 1;
          const sharePct = (lane.spend / total) * 100;
          const clickable = !!lane.persona_id;
          const hasIncremental = typeof lane.incremental_revenue === 'number' && lane.incremental_revenue > 0;
          const hasUpside = typeof lane.revenue_upside === 'number' && lane.revenue_upside > 0;
          const avgRoas = (data?.lanes ?? []).reduce(
            (s, l) => s + (typeof l.ltv_roas === 'number' ? l.ltv_roas : 0),
            0,
          ) / Math.max((data?.lanes ?? []).length, 1);
          const myRoas = typeof (lane as { ltv_roas?: number }).ltv_roas === 'number' ? (lane as { ltv_roas?: number }).ltv_roas! : 0;
          const askPhrase = avgRoas > 0 && myRoas < avgRoas ? 'underperforming' : 'outperforming';
          return (
            <li
              key={lane.persona_name}
              className="py-2.5 flex items-center justify-between gap-2 group"
            >
              <button
                disabled={!clickable}
                onClick={() =>
                  lane.persona_id &&
                  navigate(`/creative/performance/persona/${lane.persona_id}`)
                }
                className="flex-1 min-w-0 text-left disabled:cursor-not-allowed"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-navy-900 font-medium truncate">
                    {lane.persona_name}
                  </span>
                  <span className="text-[10px] font-mono text-slate-500">
                    {sharePct.toFixed(0)}%
                  </span>
                </div>
                <div className="mt-1 h-1 bg-slate-100 rounded overflow-hidden">
                  <div
                    className="h-full bg-teal-500"
                    style={{ width: `${sharePct}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono mt-1">
                  <span>{CURRENCY.format(lane.spend)} · {lane.ad_count} ads</span>
                  <span className="text-teal-600">
                    aligned {lane.aligned_spend_pct.toFixed(0)}%
                  </span>
                </div>
                {/* Incremental revenue line */}
                {(hasIncremental || hasUpside) && (
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {hasIncremental && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5">
                        <TrendingUp size={9} />
                        {CURRENCY_COMPACT.format(lane.incremental_revenue!)} captured
                      </span>
                    )}
                    {hasUpside && (
                      <span className="text-[10px] text-slate-400 font-mono">
                        +{CURRENCY_COMPACT.format(lane.revenue_upside!)} upside
                      </span>
                    )}
                  </div>
                )}
              </button>
              {onAskAboutLane && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Some lanes (e.g. Bonnard-named segments without a
                    // workbench slug) come back with persona_id=null.  Fall
                    // back to the persona_name so the analyst chat still has
                    // a label to filter on.
                    const personaKey = lane.persona_id ?? lane.persona_name;
                    onAskAboutLane({
                      question: `Why is ${lane.persona_name} ${askPhrase} right now?`,
                      persona_id: personaKey ?? undefined,
                      chart_axis: {
                        metric: 'roas',
                        persona_id: personaKey,
                        chart_type: 'area',
                        date_range_days: 30,
                      },
                    });
                  }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-teal-600 border border-teal-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1"
                  title="Ask the analyst about this persona"
                >
                  <MessageCircle size={9} />
                  Ask
                </button>
              )}
              {clickable && (
                <ArrowRight
                  size={12}
                  className="text-slate-300 group-hover:text-teal-600"
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
